import { useCallback, useEffect, useState } from 'react';
import { NumberInput } from 'react-admin';
import { useFormContext, useWatch } from 'react-hook-form';
import { Clear, CurrencyExchange } from '@mui/icons-material';
import {
	Chip,
	IconButton,
	InputAdornment,
	Stack,
	Typography
} from '@mui/material';
import { useDollarExchangeRate } from '../../../hooks/useDollarExchangeRate';
import { INPUT_SOURCE, SALE_RENT_TYPE } from '../../constants';

// Should be used inside form that has an "area" field inside itself
export const PriceByAreaInputs = ({
	title = '',
	isDisplayCurrencyRate = false,
	priceType = SALE_RENT_TYPE.RENT
}: {
	title?: string;
	isDisplayCurrencyRate?: boolean;
	priceType?: string;
}) => {
	const [isDisplayClearButton, setDisplayClearButton] = useState(false);
	const dollarRate = useDollarExchangeRate();
	const { setValue } = useFormContext();

	const DESCRIPTION_INPUT_NAME = {
		UAH_PRICE_BY_METER: `${priceType}_uah_price_by_meter`,
		UAH_OBJECT_PRICE: `${priceType}_uah_object_price`,
		USD_PRICE_BY_METER: `${priceType}_usd_price_by_meter`,
		USD_OBJECT_PRICE: `${priceType}_usd_object_price`
	};

	const [areaValue, uahPriceByMeter, usdPriceByMeter, isTerraceOn] = useWatch({
		name: [
			INPUT_SOURCE.AREA,
			DESCRIPTION_INPUT_NAME.UAH_PRICE_BY_METER,
			DESCRIPTION_INPUT_NAME.USD_PRICE_BY_METER,
			INPUT_SOURCE.TERRACE
		]
	});

	useEffect(() => {
		if (uahPriceByMeter) {
			setValue(
				DESCRIPTION_INPUT_NAME.UAH_OBJECT_PRICE,
				Math.ceil(areaValue * uahPriceByMeter)
			);
		}

		if (usdPriceByMeter) {
			setValue(
				DESCRIPTION_INPUT_NAME.USD_OBJECT_PRICE,
				Math.ceil(usdPriceByMeter * areaValue)
			);
		}
	}, [
		DESCRIPTION_INPUT_NAME.UAH_OBJECT_PRICE,
		DESCRIPTION_INPUT_NAME.USD_OBJECT_PRICE,
		isTerraceOn,
		areaValue,
		dollarRate,
		setValue,
		uahPriceByMeter,
		usdPriceByMeter
	]);

	const onPriceChangeHandler = useCallback(
		(fieldName: string, value: string) => {
			setDisplayClearButton(true);

			const newValue = Number(value) || 0;
			setValue(fieldName, Number(newValue.toFixed(2)));

			if (fieldName === DESCRIPTION_INPUT_NAME.UAH_PRICE_BY_METER) {
				setValue(
					DESCRIPTION_INPUT_NAME.UAH_OBJECT_PRICE,
					Math.ceil(areaValue * newValue)
				);
				setValue(
					DESCRIPTION_INPUT_NAME.USD_PRICE_BY_METER,
					Number((newValue / dollarRate).toFixed(2))
				);
				setValue(
					DESCRIPTION_INPUT_NAME.USD_OBJECT_PRICE,
					Math.ceil((areaValue * newValue) / dollarRate)
				);
			} else if (fieldName === DESCRIPTION_INPUT_NAME.UAH_OBJECT_PRICE) {
				setValue(
					DESCRIPTION_INPUT_NAME.UAH_PRICE_BY_METER,
					Number((newValue / areaValue).toFixed(2))
				);
				setValue(
					DESCRIPTION_INPUT_NAME.USD_PRICE_BY_METER,
					Number((newValue / areaValue / dollarRate).toFixed(2))
				);
				setValue(
					DESCRIPTION_INPUT_NAME.USD_OBJECT_PRICE,
					Math.ceil(newValue / dollarRate)
				);
			} else if (fieldName === DESCRIPTION_INPUT_NAME.USD_PRICE_BY_METER) {
				setValue(
					DESCRIPTION_INPUT_NAME.UAH_PRICE_BY_METER,
					Number((newValue * dollarRate).toFixed(2))
				);
				setValue(
					DESCRIPTION_INPUT_NAME.UAH_OBJECT_PRICE,
					Math.ceil(newValue * areaValue * dollarRate)
				);
				setValue(
					DESCRIPTION_INPUT_NAME.USD_OBJECT_PRICE,
					Math.ceil(newValue * areaValue)
				);
			} else if (fieldName === DESCRIPTION_INPUT_NAME.USD_OBJECT_PRICE) {
				setValue(
					DESCRIPTION_INPUT_NAME.UAH_PRICE_BY_METER,
					Number(((newValue / areaValue) * dollarRate).toFixed(2))
				);
				setValue(
					DESCRIPTION_INPUT_NAME.UAH_OBJECT_PRICE,
					Math.ceil(newValue * dollarRate)
				);
				setValue(
					DESCRIPTION_INPUT_NAME.USD_PRICE_BY_METER,
					Number((newValue / areaValue).toFixed(2))
				);
			}
		},
		[
			DESCRIPTION_INPUT_NAME.UAH_OBJECT_PRICE,
			DESCRIPTION_INPUT_NAME.UAH_PRICE_BY_METER,
			DESCRIPTION_INPUT_NAME.USD_OBJECT_PRICE,
			DESCRIPTION_INPUT_NAME.USD_PRICE_BY_METER,
			areaValue,
			dollarRate,
			setValue
		]
	);

	const onClear = () => {
		setValue(DESCRIPTION_INPUT_NAME.UAH_PRICE_BY_METER, '');
		setValue(DESCRIPTION_INPUT_NAME.UAH_OBJECT_PRICE, '');
		setValue(DESCRIPTION_INPUT_NAME.USD_PRICE_BY_METER, '');
		setValue(DESCRIPTION_INPUT_NAME.USD_OBJECT_PRICE, '');
		setDisplayClearButton(false);
	};

	useEffect(() => {
		if (uahPriceByMeter || usdPriceByMeter) {
			setDisplayClearButton(true);
		}
	}, []);

	return (
		<Stack gap={0}>
			<Stack justifyContent="space-between" direction="row" alignItems="center">
				<Stack gap={2} direction="row" alignItems="center">
					{title && <Typography variant="subtitle1">{title}</Typography>}
					{isDisplayCurrencyRate && (
						<Chip
							color="success"
							icon={<CurrencyExchange />}
							label={dollarRate.toFixed(2)}
						/>
					)}
				</Stack>
				<IconButton disabled={!isDisplayClearButton} onClick={onClear}>
					<Clear />
				</IconButton>
			</Stack>
			<Stack gap={2} direction="row" alignItems="center">
				<NumberInput
					readOnly={!areaValue}
					source={DESCRIPTION_INPUT_NAME.USD_PRICE_BY_METER}
					label="Ціна за м²"
					onChange={e =>
						onPriceChangeHandler(
							DESCRIPTION_INPUT_NAME.USD_PRICE_BY_METER,
							e.target.value
						)
					}
					slotProps={{
						input: {
							endAdornment: (
								<InputAdornment position="start">$/м²</InputAdornment>
							)
						}
					}}
					helperText={false}
				/>
				<NumberInput
					readOnly={!areaValue}
					slotProps={{
						input: {
							endAdornment: <InputAdornment position="start">$</InputAdornment>
						}
					}}
					onChange={e =>
						onPriceChangeHandler(
							DESCRIPTION_INPUT_NAME.USD_OBJECT_PRICE,
							e.target.value
						)
					}
					source={DESCRIPTION_INPUT_NAME.USD_OBJECT_PRICE}
					label="Ціна за об'єкт"
					helperText={false}
				/>
			</Stack>
			<Stack gap={2} direction="row" alignItems="center">
				<NumberInput
					readOnly={!areaValue}
					source={DESCRIPTION_INPUT_NAME.UAH_PRICE_BY_METER}
					onChange={e =>
						onPriceChangeHandler(
							DESCRIPTION_INPUT_NAME.UAH_PRICE_BY_METER,
							e.target.value
						)
					}
					label="Ціна за м²"
					slotProps={{
						input: {
							endAdornment: (
								<InputAdornment position="start">грн/м²</InputAdornment>
							)
						}
					}}
					helperText={false}
				/>
				<NumberInput
					readOnly={!areaValue}
					slotProps={{
						input: {
							endAdornment: (
								<InputAdornment position="start">грн</InputAdornment>
							)
						}
					}}
					onChange={e =>
						onPriceChangeHandler(
							DESCRIPTION_INPUT_NAME.UAH_OBJECT_PRICE,
							e.target.value
						)
					}
					source={DESCRIPTION_INPUT_NAME.UAH_OBJECT_PRICE}
					label="Ціна за об'єкт"
					helperText={false}
				/>
			</Stack>
		</Stack>
	);
};
